import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Indicator, IndicatorGroup, IndicatorGroupLayoutComponentProps, Shapeable } from '@shapeable/types';
import { breakpoints, ColorTokens, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { omit } from 'lodash';
import { CellLabel, ContentNode, usePageEntity } from '@shapeable/ui';
import { IndicatorLayoutKelpConditionBar } from '../indicator-layouts/indicator-layout-kelp-condition-bar';
import { Region } from '@shapeable/kelp-forest-alliance-types';
import { KelpConditionLegend } from '../elements/kelp-condition-legend';

const cls = classNames('indicator-group-layout-region-value-bars');

export type IndicatorValueVariant = 'single' | 'double';

// -------- Types -------->

// -------- Props -------->

export type IndicatorGroupLayoutRegionValueBarsProps = IndicatorGroupLayoutComponentProps & { 
  entity?: IndicatorGroup;
  title?: string;
  variant?: IndicatorValueVariant;
  colors?: ColorTokens.TextBackground;
};

export const IndicatorGroupLayoutRegionValueBarsDefaultProps: Omit<IndicatorGroupLayoutRegionValueBarsProps, 'entity'> = {
  variant: 'single',
  colors: {
    background: 'app-chrome',
    text: 'canvas',
    dark: {
      background: 'strong',
    }
  }
};

// -------- Child Component Props -------->

type ContainerProps = {
  colors: ColorTokens.TextBackground;
  _blocks: number;
}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    box-sizing: border-box;
    font-size: ${theme.FONT_SIZE(14)};
    font-family: "IBM Plex Sans", sans-serif;

    ${theme.APPLY_COLORS}
    width: 100%;
    a {
      color: inherit;
      text-decoration: underline;
    }
  `,
  tablet: css`
    font-size: ${theme.FONT_SIZE(15)};
  `,
  desktop: css`
    font-size: ${theme.FONT_SIZE(16)};
    ${theme.APPLY_COLORS}
  `
});

const TitleStyles = breakpoints({
  base: css`
    margin-bottom: ${theme.UNIT(8)};
  `,
});

const BodyStyles = breakpoints({
  base: css`
    width: 100%;
    display: flex;
    flex-direction: row;

  `,
});

const DescriptionStyles = breakpoints({
  base: css`
    
  `,
});

const ValuesStyles = breakpoints({
  base: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    color: ${theme.COLOR('mid')};
  `,
});



// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Title: styled(CellLabel)`${TitleStyles}`,
    Body: styled.div`${BodyStyles}`,
      Description: styled(ContentNode)`${DescriptionStyles}`,
      Values: styled.div`${ValuesStyles}`,

};

export const IndicatorGroupLayoutRegionValueBars: Shapeable.FC<IndicatorGroupLayoutRegionValueBarsProps> = (props) => {
  const { className, children, colors, variant, entity } = props;

  const region = usePageEntity() as Region;
  const indicatorResults = region?.indicatorResults || [];

  const indicators: Indicator[] = indicatorResults
    .filter((result) => {
      return result.indicator?.slug === 'condition-rank' && Number.isFinite(result.numericValue);
    })
    .map((result) => ({
      ...result.indicator,
      result: {
        ...result,
        indicator: {
          ...result.indicator, 
          title: result.label 
        }
      },
    } as Indicator));

  if (!indicators.length) {
    return null;
  }

  return (
    <My.Container _blocks={variant === 'single' ? 1 : 2} colors={colors} className={cls.name(className)}>
      <My.Body>
      {
        <My.Values>
        {
          indicators.map((indicator, index) => {
            return (
              <IndicatorLayoutKelpConditionBar
                key={`${indicator.id}-${index}`}
                entity={omit(indicator, 'flexInfo')}
              />
            );
          })
        }
        <KelpConditionLegend />
        </My.Values>
      }
      </My.Body>
    </My.Container>
  )
};

IndicatorGroupLayoutRegionValueBars.defaultProps = IndicatorGroupLayoutRegionValueBarsDefaultProps;
IndicatorGroupLayoutRegionValueBars.cls = cls;
